import React, { memo, useCallback, useMemo } from 'react';

import { Box } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  appraisalQcOperationOptions,
  AppraisalQcOperationType,
  FieldTypesEnum,
  getDefaultHomeVisionResubmitModeOption,
  homeVisionResubmitModeOptions,
} from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { DynamicValueInput } from '../LoanActionItems';
import SupportingDocumentsField from '../SupportingDocumentsField';

import {
  HOME_VISION_SUPPORTING_DOC_TYPE,
  HOME_VISION_SUPPORTING_DOCUMENTS_DESCRIPTION,
} from './LoanActionAppraisalQcServiceRow.constants';

const LoanActionAppraisalQcServiceRow = ({
  index,
  rootPath,
  isDisabled = false,
  isEditAllowed,
  isAutomatedActions = false,
  isSingleItem,
  documentTypesOptions,
}) => {
  const { form, setInForm, setValidationErrors } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const operationFieldPath = `${fieldPath}.operation`;
  const resubmitModeFieldPath = `${fieldPath}.resubmitMode`;
  const useJobTypeFieldPath = `${fieldPath}.useJobType`;
  const supportingDocumentsFieldPath = `${fieldPath}.supportingDocuments`;

  const areFieldsDisabled = isDisabled || !isEditAllowed;
  const forms = _.get(form, rootPath);
  const operation = _.get(form, operationFieldPath);
  const isSubmitForQc = operation?.value === AppraisalQcOperationType.SUBMIT_FOR_QC;

  // Open Order Page opens the vendor portal in a new browser tab for a human — it has no meaning for an
  // unattended automated/scheduled action, so hide it there.
  const operationOptions = useMemo(
    () =>
      isAutomatedActions
        ? appraisalQcOperationOptions.filter(
            ({ value }) => value !== AppraisalQcOperationType.OPEN_ORDER_PAGE,
          )
        : appraisalQcOperationOptions,
    [isAutomatedActions],
  );

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];

    newForms.splice(index, 1);

    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onChangeOperation = useCallback(
    (event, option) => {
      const isSubmit = option?.value === AppraisalQcOperationType.SUBMIT_FOR_QC;

      setInForm(
        resubmitModeFieldPath,
        isSubmit ? getDefaultHomeVisionResubmitModeOption(isAutomatedActions) : null,
      );
      setInForm(useJobTypeFieldPath, isSubmit ? true : null);
      setInForm(supportingDocumentsFieldPath, []);
    },
    [
      setInForm,
      resubmitModeFieldPath,
      useJobTypeFieldPath,
      supportingDocumentsFieldPath,
      isAutomatedActions,
    ],
  );

  return (
    <Box className={wrapperClassname}>
      <Box className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={areFieldsDisabled}
          label="Appraisal QC Operation"
          name={operationFieldPath}
          options={operationOptions}
          type={FieldTypesEnum.SELECT}
          onChange={onChangeOperation}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </Box>

      {isSubmitForQc && (
        <DynamicValueInput
          required
          disabled={areFieldsDisabled}
          label="Resubmit Mode"
          name={resubmitModeFieldPath}
          options={homeVisionResubmitModeOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {isSubmitForQc && (
        <DynamicValueInput
          disabled={areFieldsDisabled}
          label="Send HomeVision job type"
          name={useJobTypeFieldPath}
          type={FieldTypesEnum.BOOLEAN}
        />
      )}

      {isSubmitForQc && (
        <SupportingDocumentsField
          description={HOME_VISION_SUPPORTING_DOCUMENTS_DESCRIPTION}
          disabled={areFieldsDisabled}
          documentTypesOptions={documentTypesOptions}
          fieldPath={supportingDocumentsFieldPath}
          vendorDocType={HOME_VISION_SUPPORTING_DOC_TYPE}
        />
      )}
    </Box>
  );
};

LoanActionAppraisalQcServiceRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  isAutomatedActions: PropTypes.bool,
  documentTypesOptions: PropTypes.array,
};

export default memo(LoanActionAppraisalQcServiceRow);
